import { Alert, Stack } from '@mui/material';
import { MuiFileInput } from 'mui-file-input';
import { FC, useEffect, useRef, useState, useContext } from 'react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { authContext } from '@staffedupusa/shared-components';
import { useGetRockerboxThread } from '../hooks/useGetRockerboxThread';
import RockerboxSendMessageButtons from './RockerboxSendMessageButtons';
import RockerboxMessagesList from './RockerboxMessagesList';
import BundledEditor from '@utils/BundledEditor';

type RockerboxMessagingFormProps = {
  threadId?: number;
  companyId?: number;
  fullHeight?: boolean;
  onUnreadSuccess: () => void;
};

const RockerboxMessagingForm: FC<RockerboxMessagingFormProps> = ({
  threadId,
  companyId,
  fullHeight,
  onUnreadSuccess,
}) => {
  const [message, setMessage] = useState('');
  const [errorText, setErrorText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [threadPage, setThreadPage] = useState(1);
  const editorRef = useRef<TinyMCEEditor | null>(null);

  const { thread, messages, isLoadingThread } = useGetRockerboxThread({
    threadId,
    companyId,
    setErrorText,
    threadPage,
  });
  const {
    authData: { user },
  } = useContext(authContext);
  const loggedInUserId = user?.id;

  const onSendSuccess = () => {
    setMessage('');
    setFile(null);
    setThreadPage(1);
  };

  useEffect(() => {
    setThreadPage(1);
  }, [threadId]);

  return (
    <Stack
      spacing={2}
      sx={{
        height: {
          xs: fullHeight ? 'calc(100vh - 88px)' : 'auto',
          sm: fullHeight ? '100vh' : 'auto',
        },
      }}
    >
      {errorText && <Alert severity="error">{errorText}</Alert>}
      {messages && Boolean(messages.length) && (
        <RockerboxMessagesList
          messages={messages}
          fullHeight={fullHeight}
          headerData={thread}
          onEndReached={() => setThreadPage((prev) => prev + 1)}
          isLoading={isLoadingThread}
          loggedInUserId={loggedInUserId}
        />
      )}
      {/* <SavedMessagesSelect setMessage={setMessage} message={message} /> */}
      <Stack
        spacing={3}
        sx={{
          pt: 0,
          pb: 3,
          px: 3,
          borderRadius: 0,
          boxShadow: 'none',
          borderTop: '1px solid rgba(128, 129, 140, .25)',
        }}
        style={{ marginTop: '0' }}
      >
        <BundledEditor
          onInit={(evt: any, editor: TinyMCEEditor | null) =>
            (editorRef.current = editor)
          }
          value={message}
          onEditorChange={(newValue:string) => setMessage(newValue)}
          init={{
            height: 180,
            menubar: false,
            plugins: ['lists', 'autolink', 'link', 'pagebreak'],
            toolbar: 'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
            spellchecker_dialog: true,
            spellchecker_rpc_url: 'localhost/ephox-spelling',
            spellchecker_language: 'en_US',
            spellchecker_active: true,
            browser_spellcheck: true,
            contextmenu: false
          }}
        />
        <MuiFileInput
          variant="outlined"
          size="small"
          label="Attach files (pdf, doc, docx, jpg, jpeg, png)"
          value={file!}
          onChange={(newFile) => setFile(newFile)}
          InputProps={{
            placeholder: 'true',
            inputProps: {
              accept: '.pdf, .doc, .docx, .jpg, .jpeg, .png',
              placeholder: 'false',
            },
          }}
        />
        <RockerboxSendMessageButtons
          message={message}
          threadId={threadId}
          companyId={companyId}
          setErrorText={setErrorText}
          onSendSuccess={onSendSuccess}
          file={file}
          recipientId={thread?.participant1?.id}
          onUnreadSuccess={onUnreadSuccess}
        />
      </Stack>
    </Stack>
  );
};

export default RockerboxMessagingForm;
